import { supabase } from '@/core/supabase/client';
import {
  guardarEnCola,
  leerCola,
  leerDeCola,
  quitarDeCola,
  type AltaEnCola,
  type ResumenAlta,
} from './almacen';
import { uuidV4 } from '@/core/uuid';

/**
 * La cola de altas que todavía no confirmó el servidor.
 *
 * Una sola regla la gobierna, y de ella se derivan todas las demás:
 *
 *     un borrador NO se borra hasta que el servidor confirma que existe.
 *
 * No cuando el envío "parece" haber salido, no cuando el teléfono cree tener
 * señal, no cuando pasó un rato. Sólo con la respuesta en la mano.
 */

/** Qué clase de fracaso fue. Cambia por completo qué corresponde hacer después. */
export type ClaseDeFallo =
  /** No llegó a destino: sin señal, red caída, tiempo agotado. Se reintenta. */
  | 'sin_respuesta'
  /** El servidor contestó que no. Reintentar solo daría el mismo resultado. */
  | 'rechazado';

/**
 * Distingue "no llegó" de "llegó y lo rechazaron".
 *
 * La diferencia decide si reintentar sirve de algo. Un mes cerrado va a seguir
 * cerrado dentro de una hora: insistir contra eso es gastar batería y esconder
 * el problema detrás de un reintento eterno. Un corte de señal, en cambio, se
 * arregla solo cuando vuelve la conexión.
 *
 * PostgREST devuelve el `code` de PostgreSQL cuando la petición llegó; sin
 * respuesta no hay código que devolver.
 */
export function clasificarFallo(error: unknown): ClaseDeFallo {
  const e = error as { code?: unknown; message?: unknown } | null;
  const codigo = typeof e?.code === 'string' ? e.code : '';
  // Los códigos de red de Supabase no son SQLSTATE. Un SQLSTATE tiene cinco
  // caracteres alfanuméricos y significa que la base llegó a opinar.
  if (/^[0-9A-Z]{5}$/.test(codigo)) return 'rechazado';

  const mensaje = typeof e?.message === 'string' ? e.message.toLowerCase() : '';
  if (mensaje.includes('failed to fetch') || mensaje.includes('networkerror')) {
    return 'sin_respuesta';
  }
  if (mensaje.includes('load failed') || mensaje.includes('timeout')) {
    return 'sin_respuesta';
  }
  // Ante la duda, se trata como rechazo: deja el caso a la vista en lugar de
  // reintentarlo en silencio para siempre.
  return 'rechazado';
}

/**
 * Anota un alta para enviar. Devuelve el identificador de la operación, que ya
 * queda guardado en el dispositivo: sobrevive a cerrar la aplicación.
 */
export async function encolarAlta(input: {
  organizationId: string;
  payload: Record<string, unknown>;
  resumen: ResumenAlta;
}): Promise<AltaEnCola> {
  const clientRequestId = uuidV4();
  const alta: AltaEnCola = {
    clientRequestId,
    organizationId: input.organizationId,
    // El identificador viaja DENTRO del cuerpo, y se fija una sola vez. Los
    // reintentos mandan exactamente esto, sin volver a generarlo.
    payload: { ...input.payload, client_request_id: clientRequestId },
    estado: 'pendiente',
    resumen: input.resumen,
    creadoEn: Date.now(),
    intentos: 0,
    ultimoIntento: null,
    ultimoError: null,
    workSessionId: null,
  };
  await guardarEnCola(alta);
  return alta;
}

/** Resultado de intentar enviar UNA alta. */
export type ResultadoEnvio =
  | { estado: 'sincronizado'; workSessionId: string }
  | { estado: 'pendiente'; motivo: string }
  | { estado: 'error'; motivo: string };

/**
 * Intenta enviar un alta.
 *
 * Nunca borra nada: sólo deja anotado cómo le fue. Quien decide sacar el
 * borrador de la cola es quien ya vio el identificador del trabajo creado.
 */
export async function intentarEnviar(clientRequestId: string): Promise<ResultadoEnvio> {
  const alta = await leerDeCola(clientRequestId);
  if (!alta) return { estado: 'error', motivo: 'El borrador ya no está en el dispositivo' };
  if (alta.estado === 'sincronizado' && alta.workSessionId) {
    return { estado: 'sincronizado', workSessionId: alta.workSessionId };
  }

  await guardarEnCola({ ...alta, estado: 'enviando', ultimoIntento: Date.now() });

  const { data, error } = await supabase.rpc('create_work_session', { p: alta.payload });

  if (error) {
    const clase = clasificarFallo(error);
    const motivo = error.message ?? 'No se pudo enviar';
    await guardarEnCola({
      ...alta,
      // Sin respuesta vuelve a la fila y se reintenta solo. Rechazado se queda
      // quieto, con el motivo a la vista, hasta que alguien decida qué hacer.
      estado: clase === 'sin_respuesta' ? 'pendiente' : 'error',
      intentos: alta.intentos + 1,
      ultimoIntento: Date.now(),
      ultimoError: motivo,
    });
    return clase === 'sin_respuesta'
      ? { estado: 'pendiente', motivo }
      : { estado: 'error', motivo };
  }

  const workSessionId = data as string;
  await guardarEnCola({
    ...alta,
    estado: 'sincronizado',
    intentos: alta.intentos + 1,
    ultimoIntento: Date.now(),
    ultimoError: null,
    workSessionId,
  });
  return { estado: 'sincronizado', workSessionId };
}

/**
 * Recorre la cola de una organización e intenta enviar lo que corresponda.
 *
 * Los rechazados no entran: ya se sabe qué contestó el servidor y volver a
 * preguntar no cambia la respuesta. Cada alta se intenta por separado, así una
 * atascada no tapa a las que sí pueden salir.
 */
export async function sincronizarCola(
  organizationId: string,
  alTerminarUna?: (alta: AltaEnCola, resultado: ResultadoEnvio) => void,
): Promise<{ sincronizadas: number; pendientes: number; conError: number }> {
  const cola = await leerCola(organizationId);
  let sincronizadas = 0;
  let pendientes = 0;
  let conError = 0;

  for (const alta of cola) {
    if (alta.estado === 'error') {
      conError++;
      continue;
    }
    if (alta.estado === 'sincronizado') {
      sincronizadas++;
      continue;
    }

    const resultado = await intentarEnviar(alta.clientRequestId);
    if (resultado.estado === 'sincronizado') sincronizadas++;
    else if (resultado.estado === 'pendiente') pendientes++;
    else conError++;

    alTerminarUna?.(alta, resultado);

    // Si se cortó la señal en el medio, no tiene sentido seguir castigando la
    // batería con el resto de la fila.
    if (resultado.estado === 'pendiente') break;
  }

  return { sincronizadas, pendientes, conError };
}

/** Confirma la baja de un borrador ya sincronizado. */
export async function confirmarSincronizada(clientRequestId: string): Promise<void> {
  const alta = await leerDeCola(clientRequestId);
  // La guarda importa: sin ella, un error de programación podría borrar un
  // borrador que nunca llegó al servidor.
  if (alta?.estado === 'sincronizado' && alta.workSessionId) {
    await quitarDeCola(clientRequestId);
  }
}

/** Vuelve a poner en la fila algo que el servidor había rechazado. */
export async function reintentarRechazada(clientRequestId: string): Promise<void> {
  const alta = await leerDeCola(clientRequestId);
  if (!alta || alta.estado !== 'error') return;
  await guardarEnCola({ ...alta, estado: 'pendiente' });
}

export { leerCola, quitarDeCola };
export type { AltaEnCola, ResumenAlta };
